//@ts-ignore
import { boundingExtent } from 'ol/extent'
import { AloYo, IAloYoConstParam } from '@/models/alo/AloYo'
import { extent } from 'd3'
import { autoScaleDomain3, autoScaleDomain, mustIgnoreValue } from '@/utils/autoscale'
import { Plotvariable } from '@/models/ui/Plotvariable'
import { dataLimitMode } from '@/models/ui/GraphUiState'
import { AloBaseArray, IAloBaseArray } from './AloBaseArray'
import { AloMesureScience } from './AloMesureScience'
import { AloGliderFileShort } from './AloGliderFileShort'
import { AloDbMesureType } from '../alodb/AloDbMesureType'
import { AloDbMesureDataname } from '../alodb/AloDbMesureDataname'
import { AloSecurityItem } from './AloSecurityItem'
import { calculSecurityRights } from '@/enum/SecurityItems'
import { AloLogbookItem } from './AloLogbookItem'
import { AloMissionPlan } from './AloMissionPlan'
import { AloDataRawSet } from './AloDataRawSet'
import { IGliderRuntime } from '../iris/gliderState/IGliderRuntime'
import { isNumber } from 'lodash'
import { ISmartResult, getDefaultSmartResult } from '../iris/smartMission/ISmartResult'
import { ISmartMission, getDefaultSmartMission } from '../iris/smartMission/ISmartMission'
import distance from '@turf/distance'
import { point } from '@turf/helpers'

export enum GLIDER_MODE {
  GLIDER_MODE_DISCONNECTED = 'Disconnected',
  GLIDER_MODE_WAITING_FOR_GO = 'WaitingForGo',
  GLIDER_MODE_WAITING_FOR_RESUME = 'WaitingForResume',
  GLIDER_MODE_WAITING_FOR_ACK = 'WaitingForAck',
}

export const gliderWaitingModesColor = {
  [GLIDER_MODE.GLIDER_MODE_DISCONNECTED]: '#999999',
  [GLIDER_MODE.GLIDER_MODE_WAITING_FOR_GO]: '#15FF00',
  [GLIDER_MODE.GLIDER_MODE_WAITING_FOR_RESUME]: '#000fe3',
  [GLIDER_MODE.GLIDER_MODE_WAITING_FOR_ACK]: '#ffa500',
}

export class AloMission {
  public misId: number | null
  public misIdx: number | null
  public misLibelle: string | null
  public gliId: number | null
  public ensId: number | null
  public ensKey: number | null
  public ensLibelle: string | null
  public fleId: number | null
  public gliderWaitingMode: string
  public gliKey: string | null
  public gliFabricant: string | null //modèle du glider (GLI_FABRICANT, ex. 'SXP'/'DXP') — filtrage complétion de commandes
  public misActifOn: boolean
  public isTestMission: boolean
  public isGeoHidden: boolean
  public aisStatus: 'OFF' | 'NO_RISK' | 'RISK' | 'NO_DATA'
  public alarmStatusLast: Array<string>
  public yoArray: Array<AloYo>
  public backInDateTime: Date | null
  public mesureTypeExArray: Array<AloDbMesureType>
  public mesureDatanameExArray: Array<AloDbMesureDataname>
  public mapLayerIds: Array<number>
  public nmeaTypeArray: Array<string>
  public _detailNmeaLoaded: { [nmea: string]: boolean }
  public _detailLoaded: boolean
  public _dataScienceLoaded: boolean
  public _dataScienceLoading: boolean
  public _dataNavigationLoaded: boolean
  public _dataNavigationLoading: boolean
  public _driftLoaded: boolean
  public _downloadFileStatusLoaded: boolean
  public _logBookLoaded: boolean
  public _missionPlanLoaded: boolean
  public logBookItems: Array<AloLogbookItem>
  public missionPlanStaging: AloMissionPlan | null
  public gliderRuntime: IGliderRuntime | null
  public domains: any
  public domainsAutoScale: any
  public domainsColor: any
  public dataNmea: { [nmea: string]: AloDataRawSet }
  public dataScience: Array<any>
  public dataNavigation: Array<any>
  public dataSimulation: Array<any>
  public downloadFileStatus: Array<AloGliderFileShort> | null
  public sciPlotVariables: Array<Plotvariable>
  public xSciScatterVariable: string
  public ySciScatterVariable: string
  public ySciProfileVariable: string
  public xNavScatterVariable: string
  public xSimuScatterVariable: string
  public navPlotVariables: Array<Plotvariable>
  public simuPlotVariables: Array<Plotvariable>
  public roles: Array<string> //role de l'utilisateur identifié pour la mission
  public security: { [index: string]: AloSecurityItem }
  public _extent: any
  public ad2cpDataIntegrationState: string
  public _refresh: boolean
  public isVirtual: boolean
  public vimDatexec: Date | null
  public vimParamJson: string | null
  public vimResultJson: string | null
  public vimIteration: number | null
  public vimStatus: number | null
  public result: ISmartResult | null
  public param: ISmartMission | null
  public simulation: AloMission | null
  public misLbkAlertStatus: number
  public hasEndMissionSimulation: boolean
  public covId: number | null

  constructor({
    misId = null,
    misIdx = null,
    misLibelle = null,
    gliId = null,
    ensId = null,
    ensKey = null,
    ensLibelle = null,
    fleId = null,
    gliderWaitingMode = GLIDER_MODE.GLIDER_MODE_DISCONNECTED,
    gliKey = null,
    gliFabricant = null as string | null,
    misActifOn = false,
    isTestMission = false,
    isGeoHidden = false,
    aisStatus = 'OFF' as 'OFF' | 'NO_RISK' | 'RISK' | 'NO_DATA',
    alarmStatusLast = 'Ok',
    yoArray = [],
    backInDateTime = null as string | null,
    mesureTypeExArray = [],
    mapLayerIds = [],
    nmeaTypeArray = [],
    mesureDatanameExArray = [],
    _detailNmeaLoaded = {},
    detailLoaded = false,
    dataNmea = {},
    dataScienceLoaded = false,
    dataScienceLoading = false,
    dataNavigationLoaded = false,
    dataNavigationLoading = false,
    driftLoaded = false,
    downloadFileStatusLoaded = false,
    logBookLoaded = false,
    missionPlanLoaded = false,
    logBookItems = [],
    missionPlanStaging = null,
    gliderRuntime = null as IGliderRuntime | null,
    dataScience = [],
    dataNavigation = [],
    dataSimulation = [],
    domains = {},
    domainsAutoScale = {},
    domainsColor = {},
    downloadFileStatus = [],
    roles = [],
    isVirtual = false,
    misLbkAlertStatus = 0,
    covId = null,
    vimParamJson = null as string | null,
    vimDatexec = null,
    vimResultJson = null as string | null,
    vimStatus = null,
    vimIteration = null as number | null,
  } = {}) {
    this.covId = covId
    this._refresh = false
    this.misId = misId
    this.misIdx = misIdx
    this.misLibelle = misLibelle
    this.gliId = gliId
    this.ensId = ensId
    this.ensKey = ensKey
    this.ensLibelle = ensLibelle
    this.fleId = fleId
    this.gliderWaitingMode = gliderWaitingMode
    this.gliKey = gliKey
    this.gliFabricant = gliFabricant
    this.misActifOn = misActifOn
    this.isTestMission = isTestMission
    this.isGeoHidden = isGeoHidden
    this.aisStatus = aisStatus
    this.isVirtual = isVirtual
    this.vimParamJson = (vimParamJson?.length ?? 0) > 0 ? vimParamJson : null
    this.vimResultJson = (vimResultJson?.length ?? 0) > 0 ? vimResultJson : null
    //@ts-ignore
    this.vimDatexec = vimDatexec ? new Date(vimDatexec + 'Z') : null
    this.vimIteration = vimIteration ?? 0
    this.vimStatus = vimStatus
    this.result = this.vimResultJson
      ? ({ ...getDefaultSmartResult(), ...JSON.parse(this.vimResultJson) } as ISmartResult)
      : null
    this.param = this.vimParamJson
      ? ({ ...getDefaultSmartMission(), ...JSON.parse(this.vimParamJson ?? '{}') } as ISmartMission)
      : null
    this.hasEndMissionSimulation = !!this.param || !!this.result
    this.isVirtual = isVirtual
    this.misLbkAlertStatus = misLbkAlertStatus
    this.alarmStatusLast = alarmStatusLast?.toString()?.split(', ') ?? ['Ok']
    this.yoArray = (yoArray ?? [])
      .map(yo => {
        const yof = new AloYo(yo)
        Object.freeze(yof)
        return yof
      })
      .sort(function (a, b) {
        return a.yoGpsDatTime.getTime() - b.yoGpsDatTime.getTime()
      })
    this.backInDateTime = backInDateTime ? new Date(backInDateTime!) : null
    //Patch temporaire le temps que Mik envoie les yos manquant meme si non parsé dans les données
    //injecte les yos manquants
    let index = 1
    while (index < this.yoArray.length) {
      let startCycle = this.yoArray[index - 1].yosCycle! + 1
      const endCycle = this.yoArray[index].yosCycle!
      const loopIndex = index
      while (startCycle < endCycle) {
        this.yoArray.splice(index, 0, new AloYo({ yosCycle: startCycle } as IAloYoConstParam))
        startCycle++
        index++
      }
      if (loopIndex == index) index++
    }

    this.mesureTypeExArray = mesureTypeExArray.map(m => AloDbMesureType.createAloDbMesureTypeWithDetail(m))
    this.mesureDatanameExArray = mesureDatanameExArray.map(m =>
      AloDbMesureDataname.createAloDbMesureDatanameWithDetail(m),
    )
    this.mapLayerIds = mapLayerIds
    this.nmeaTypeArray = nmeaTypeArray
    this._detailNmeaLoaded = _detailNmeaLoaded
    this._detailLoaded = detailLoaded
    this._dataScienceLoaded = dataScienceLoaded
    this._dataScienceLoading = dataScienceLoading
    this._dataNavigationLoaded = dataNavigationLoaded
    this._dataNavigationLoading = dataNavigationLoading
    this._driftLoaded = driftLoaded
    this._downloadFileStatusLoaded = downloadFileStatusLoaded
    this._logBookLoaded = logBookLoaded
    this._missionPlanLoaded = missionPlanLoaded
    this.logBookItems = logBookItems
    this.missionPlanStaging = missionPlanStaging
    this.gliderRuntime = gliderRuntime
    this.domains = domains
    this.domainsAutoScale = domainsAutoScale
    this.domainsColor = domainsColor
    this.dataNmea = dataNmea
    this.dataScience = dataScience
    this.dataNavigation = dataNavigation
    this.dataSimulation = dataSimulation
    this.downloadFileStatus = downloadFileStatus
    this.xSciScatterVariable = 'PLD_REALTIMECLOCK'
    this.ySciScatterVariable = 'NAV_DEPTH'
    this.ySciProfileVariable = 'NAV_DEPTH'
    this.xNavScatterVariable = 'datNav'
    this.xSimuScatterVariable = 'datSimu'
    this.ad2cpDataIntegrationState = ''
    this.navPlotVariables = [
      new Plotvariable({
        name: 'Date',
        variable: 'datNav',
        unit: '',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'date',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Depth - Altitude',
        variable: 'depthAlt',
        variables: ['depth', 'bottomDepth'],
        infosVariables: ['Depth', 'Altitude'],
        unit: 'm',
        invertedYAxis: true,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'Depth',
        variable: 'depth',
        unit: 'm',
        invertedYAxis: true,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Altitude',
        variable: 'altitude',
        unit: 'm',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Voltage',
        variable: 'voltage',
        unit: 'V',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'SecurityLevel',
        variable: 'securityLevel',
        unit: '',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'Pitch',
        variable: 'pitch',
        unit: '°',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'Roll',
        variable: 'roll',
        unit: '°',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'Heading Cmd',
        variable: 'desiredH',
        unit: '°',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Heading Pos',
        variable: 'heading',
        unit: '°',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Heading',
        variable: 'head',
        variables: ['heading', 'desiredH'],
        infosVariables: ['Position', 'Command'],
        unit: '°',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'Ballast Cmd',
        variable: 'ballastCmd',
        unit: '',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Ballast Pos',
        variable: 'ballastPost',
        unit: '',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Ballast',
        variable: 'ballast',
        variables: ['ballastPost', 'ballastCmd'],
        infosVariables: ['Position', 'Command'],
        unit: '',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'Lin Cmd',
        variable: 'linCmd',
        unit: 'mm',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Lin Pos',
        variable: 'linPos',
        unit: 'mm',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Lin',
        variable: 'lin',
        variables: ['linPos', 'linCmd'],
        infosVariables: ['Position', 'Command'],
        unit: 'mm',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'Ang Cmd',
        variable: 'angCmd',
        unit: '°',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Ang Pos',
        variable: 'angPos',
        unit: '°',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Ang',
        variable: 'ang',
        variables: ['angPos', 'angCmd'],
        infosVariables: ['Position', 'Command'],
        unit: '°',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'InternalTemperature',
        variable: 'temp',
        unit: '°',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'InternalPressure',
        variable: 'pa',
        unit: 'Pa',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'NavState',
        variable: 'state',
        unit: '',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'Altitude',
        variable: 'altitude',
        unit: 'm',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'VerticalSpeed',
        variable: 'verticalSpeed',
        unit: 'cm/s',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
      new Plotvariable({
        name: 'PortsideThruster',
        variable: 'thrusterPort',
        unit: '%',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'StarboardThruster',
        variable: 'thrusterStbd',
        unit: '%',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Thrusters',
        variable: 'thrusters',
        variables: ['thrusterPort', 'thrusterStbd'],
        infosVariables: ['Portside', 'Starboard'],
        unit: '%',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'number',
        autoPlot: true,
      }),
    ]

    this.simuPlotVariables = [
      new Plotvariable({
        name: 'Date',
        variable: 'datSimu',
        unit: '',
        invertedYAxis: false,
        logarithmeAxis: false,
        typeData: 'date',
        autoPlot: false,
        colors: ['#fb8c00'],
      }),
      new Plotvariable({
        name: 'Duration',
        variable: 'duration',
        unit: 'h',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: true,
        colors: ['#fb8c00'],
      }),
      new Plotvariable({
        name: 'Cumulated duration',
        variable: 'durationTotal',
        unit: 'days',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: true,
        colors: ['#fb8c00'],
      }),
      new Plotvariable({
        name: 'Distance',
        variable: 'distance',
        unit: 'km',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: true,
        colors: ['#fb8c00'],
      }),
      new Plotvariable({
        name: 'Cumulated distance',
        variable: 'distanceTotal',
        unit: 'km',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: true,
        colors: ['#fb8c00'],
      }),
      new Plotvariable({
        name: 'Speed',
        variable: 'speed',
        unit: 'km/h',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: true,
        colors: ['#fb8c00'],
      }),
      new Plotvariable({
        name: 'Energy',
        variable: 'energy',
        unit: '%',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: true,
        colors: ['#fb8c00'],
      }),
      new Plotvariable({
        name: 'Yos',
        variable: 'yosCycle',
        unit: '',
        invertedYAxis: false,
        typeData: 'number',
        autoPlot: true,
        colors: ['#fb8c00'],
      }),
    ]

    //on fusionne le tableau des mesureTypeExArray et on ajoute les mesureDatanameExArray non present
    this.sciPlotVariables = [
      new Plotvariable({
        name: 'Date',
        variable: 'PLD_REALTIMECLOCK',
        unit: '',
        invertedYAxis: false,
        typeData: 'date',
        autoPlot: false,
      }),
      new Plotvariable({
        name: 'Depth',
        colors: ['#ec7404'],
        variable: 'NAV_DEPTH',
        unit: 'm',
        invertedYAxis: true,
        typeData: 'number',
        autoPlot: false,
      }),
      ...this.mesureTypeExArray.map(m => {
        return new Plotvariable({
          ...{
            name: m.MET_KEY, //m.MET_LIBELLE, //TODO remetre met_libelle si on veux que la legende des graphs change
            variable: m.MET_KEY,
            unit: m.mesureUniteEx!.MUT_SYMBOL,
            colors: ['#ec7404'],
            typeData: 'number',
            invertedYAxis: false,
            logarithmeAxis: false,
            autoPlot: true,
          },
          ...(m.MET_CONFIG_JSON ? JSON.parse(m.MET_CONFIG_JSON) : {}),
        })
      }),
      //on ajoute les variables de plots non affectés
      ...this.mesureDatanameExArray
        .filter(m => this.mesureTypeExArray.every(mt => mt.MET_KEY != m.MDN_KEY))
        .map(m => {
          return new Plotvariable({
            ...{
              name: m.libelle!,
              variable: m.MDN_KEY!,
              unit: '',
              colors: ['#ec7404'],
              typeData: 'number',
              invertedYAxis: false,
              logarithmeAxis: false,
              autoPlot: true,
            } /*,
                        //@ts-ignore
                        ...(window.mesureTypesByName && window.mesureTypesByName[m.MDN_KEY] && window.mesureTypesByName[m.MDN_KEY].MET_CONFIG_JSON?JSON.parse(window.mesureTypesByName[m.MDN_KEY].MET_CONFIG_JSON):{})*/,
          })
        }),
    ].sort((a, b) => (a.name > b.name ? 1 : -1))

    // this.sciPlotVariables = [
    //     new Plotvariable({name:'Date',variable:'PLD_REALTIMECLOCK',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'date', autoPlot:false }),
    //     new Plotvariable({name:'Temperature', colors:["#ec7404"], variable:'GPCTD_TEMPERATURE',unit:'°C',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     new Plotvariable({name:'Pressure', colors:["#ec7404"], variable:'GPCTD_PRESSURE',unit:'dBar',invertedYAxis:true,logarithmeAxis:false, typeData:'number', autoPlot:false }),
    //     new Plotvariable({name:'Depth', colors:["#ec7404"], variable:'NAV_DEPTH',unit:'m',invertedYAxis:true,logarithmeAxis:false, typeData:'number', autoPlot:false }),
    //     new Plotvariable({name:'Conductivity', colors:["#ec7404"], variable:'GPCTD_CONDUCTIVITY',unit:'mS s-1',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     new Plotvariable({name:'DOF', colors:["#ec7404"], variable:'GPCTD_DOF',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     /*new Plotvariable({name:'FLNTU_CHL_COUNT', colors:["#ec7404"], variable:'FLNTU_CHL_COUNT',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     new Plotvariable({name:'FLNTU_CHL_SCALED', colors:["#ec7404"], variable:'FLNTU_CHL_SCALED',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     new Plotvariable({name:'FLNTU_NTU_COUNT', colors:["#ec7404"], variable:'FLNTU_NTU_COUNT',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     new Plotvariable({name:'FLNTU_NTU_SCALED', colors:["#ec7404"], variable:'FLNTU_NTU_SCALED',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),*/
    //     new Plotvariable({name:'FLBBCD_CHL_COUNT', colors:["#ec7404"], variable:'FLBBCD_CHL_COUNT',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     new Plotvariable({name:'FLBBCD_CHL_SCALED', colors:["#ec7404"], variable:'FLBBCD_CHL_SCALED',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     new Plotvariable({name:'FLBBCD_BB_700_COUNT', colors:["#ec7404"], variable:'FLBBCD_BB_700_COUNT',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true }),
    //     new Plotvariable({name:'FLBBCD_BB_700_SCALED', colors:["#ec7404"], variable:'FLBBCD_BB_700_SCALED',unit:'',invertedYAxis:false,logarithmeAxis:false, typeData:'number', autoPlot:true })
    // ]

    //Calculs des données de simulation
    const surfacingYos = this.yoArray.filter(yo => !yo.isUnderWater && isNumber(yo.yoGpsLat) && isNumber(yo.yoGpsLon))
    if (surfacingYos.length == 0) this.dataSimulation = []
    else {
      //@ts-ignore
      let prevYo = surfacingYos.at(0)!
      const datas = []
      let distanceTotal = 0
      let durationTotal = 0
      for (let yo of surfacingYos) {
        const duration = (yo.yoGpsDatTime.getTime() - prevYo.yoGpsDatTime.getTime()) / (60 * 60 * 1000)
        const dist = distance(point([prevYo.yoGpsLon!, prevYo.yoGpsLat!]), point([yo.yoGpsLon!, yo.yoGpsLat!]))
        distanceTotal += dist
        durationTotal += duration
        datas.push({
          datSimu: yo.yoGpsDatTime,
          duration: Number.parseFloat(duration.toFixed(2)),
          distance: Number.parseFloat(dist.toFixed(2)),
          distanceTotal: Number.parseFloat(distanceTotal.toFixed(2)),
          durationTotal: Number.parseFloat((durationTotal / 24).toFixed(2)),
          speed: duration > 0 ? Number.parseFloat((dist / duration).toFixed(3)) : 0,
          energy: yo.batteries,
          yosCycle: yo.yosCycle,
        })
        prevYo = yo
      }
      this.dataSimulation = datas
    }

    calculAllDomainsData(
      this.simuPlotVariables,
      this.xSimuScatterVariable,
      this.domains,
      this.domainsAutoScale,
      this.domainsColor,
      this.dataSimulation,
    )

    this.roles = roles
    this.security = calculSecurityRights(roles)
    updateAloMissionExtent(this)
    this.simulation = this.vimParamJson ? AloMission.buildSimulation(this) : null
  }

  static buildSimulation(missionvirtual: AloMission, detailLoaded = false) {
    const mesureTypeExArray = [
      {
        MET_KEY: 'SIMU_TEMPERATURE',
        mesureUniteEx: { MUT_SYMBOL: '°' },
        MET_CONFIG_JSON: JSON.stringify({ name: 'Température' }),
      },
      {
        MET_KEY: 'SIMU_SALINITY',
        mesureUniteEx: { MUT_SYMBOL: 'PSU' },
        MET_CONFIG_JSON: JSON.stringify({ name: 'Salinity' }),
      },
    ]

    //@ts-ignore
    const lastYoCycle = Math.max(missionvirtual?.param?.current_cycle ?? 1, 1)

    //@ts-ignore
    const simulation = new AloMission({
      //@ts-ignore
      detailLoaded: detailLoaded || missionvirtual.vimStatus < 0 || missionvirtual.vimStatus == 10,
      //@ts-ignore
      misId: -missionvirtual.misId,
      //@ts-ignore
      misIdx: missionvirtual.misIdx,
      //@ts-ignore
      misLibelle: missionvirtual.misLibelle,
      //@ts-ignore
      gliId: missionvirtual.gliId,
      //@ts-ignore
      ensId: missionvirtual.ensId,
      //@ts-ignore
      yoArray: (missionvirtual.result?.surfacing_points ?? []).map((s, idx) => ({
        yosId: -idx,
        //@ts-ignore
        yosCycle: s?.at(0) + (lastYoCycle - 1),
        heading: s?.at(4),
        cr: s?.at(6),
        cf: s?.at(5),
        yoGpsLat: s?.at(2),
        yoGpsLon: s?.at(3),
        yoGpsDatTime: s?.at(1),
        batteries: s?.at(7),
      })),
      //@ts-ignore
      isVirtual: true,
      //@ts-ignore
      mesureTypeExArray,
    })

    //on prend en compte les points du mission plan pour la zone de zoom auto
    const gpsPoint =
      missionvirtual.param?.mission_plan.wpts
        ?.filter(wpt => wpt.wpt_lat != null && wpt.wpt_lon != null)
        ?.map(wpt => [wpt.wpt_lon, wpt.wpt_lat]) ?? []
    //@ts-ignore
    const yos =
      simulation.yoArray.filter(y => y.yoGpsLat != null && y.yoGpsLon != null)?.map(y => [y.yoGpsLon!, y.yoGpsLat!]) ??
      []
    simulation._extent = boundingExtent([...gpsPoint, ...yos])

    //reaafecte l'état des données science et de navigation si la mission etait finished ou aborted avant
    if (
      missionvirtual &&
      (missionvirtual.result?.simulation_state == 'finished' || missionvirtual.result?.simulation_state == 'aborted')
    ) {
      simulation._dataScienceLoaded = missionvirtual?.simulation?._dataScienceLoaded ?? false
      simulation._dataScienceLoading = missionvirtual?.simulation?._dataScienceLoading ?? false
      simulation.dataScience = missionvirtual?.simulation?.dataScience ?? []

      simulation._dataNavigationLoaded = missionvirtual?.simulation?._dataNavigationLoaded ?? false
      simulation._dataNavigationLoading = missionvirtual?.simulation?._dataNavigationLoading ?? false
      simulation.dataNavigation = missionvirtual?.simulation?.dataNavigation ?? []
    }

    return simulation
  }

  static createAloMissionArray(datas: IAloBaseArray) {
    const aloResult = new AloBaseArray(AloMission, datas)
    //TODO à virer une fois le bug corrigé par Mik
    aloResult.items = aloResult.items.filter(
      m =>
        !(
          (m.roles.length == 2 && m.roles.some(r => r == 'adcp') && m.roles.some(r => r == 'guest')) ||
          (m.roles.length == 1 && m.roles.some(r => r == 'guest'))
        ),
    )
    return aloResult
  }

  static createAloMissionWithDetail(data: any) {
    return new AloMission({ ...data, detailLoaded: true })
  }
}

export const updateAloMissionExtent = (mission: AloMission) => {
  mission._extent = boundingExtent(
    mission.yoArray
      .filter(y => y.yoGpsLat != null && y.yoGpsLon != null)
      .map(y => [y.yoGpsLon, y.yoGpsLat] as [number, number]),
  )
}

export const buildScienceDataRowReadOnly = (measure: AloMesureScience) => {
  const mes = measure.mesureData.reduce(
    (acc, m) => {
      acc[m.key] = m.value
      return acc
    },
    {
      yosId: measure.yosId,
      yosCycle: measure.yosCycle,
      PLD_REALTIMECLOCK: new Date(measure.datPld),
      NAV_DEPTH: measure.navDepth,
    } as any,
  )
  //evite la creation des porprietés reactives dans vuex
  Object.freeze(mes)
  return mes
}

export const arrayToDataScienceArr = (datas: IAloBaseArray) => {
  return datas.items.map(m => buildScienceDataRowReadOnly(m))
}

export const arrayToDataNavigationArr = (datas: IAloBaseArray) => {
  if (datas.items.length > 0) {
    let minBootom = 0
    datas.items.forEach((d, index) => {
      d.datNav = new Date(d.datNav)
      //Calcul du BottomDepth
      d.bottomDepth = d.altitude != 0 && d.altitude != -1 ? parseFloat(d.depth) + parseFloat(d.altitude) : 0
      if (d.depth > minBootom) minBootom = d.depth
      if (d.bottomDepth > minBootom) minBootom = d.bottomDepth
      //Calcul du Vertical Speed
      //d.verticalSpeed = index < 5?0: 100000.0 * (d.depth - datas.items[index - 5].depth) / ( d.datNav.getTime() -  datas.items[index - 5].datNav.getTime())
      d.verticalSpeed =
        index < 1
          ? 0
          : (100000.0 * (d.depth - datas.items[index - 1].depth)) /
            (d.datNav.getTime() - datas.items[index - 1].datNav.getTime())
    })
    //on met à jour les point ou on avait pas de fond détecté (BottomDepth == 0)
    datas.items.forEach(d => {
      if (d.bottomDepth == 0) d.bottomDepth = minBootom + 100
      //evite la creation des porprietés reactives dans vuex
      Object.freeze(d)
    })
  }
  return datas.items
}

export const calculAllDomainsData = (
  plotVariables: Array<Plotvariable>,
  dateVariableName: string,
  domains: any,
  domainsAutoScale: any,
  domainsColor: any,
  datas: Array<any>,
) => {
  if (datas.length > 0) {
    //GENERE DOMAINE FOR ALL DATA
    if (!domains[dataLimitMode.ALL]) domains[dataLimitMode.ALL] = {}
    if (!domainsAutoScale[dataLimitMode.ALL]) domainsAutoScale[dataLimitMode.ALL] = {}
    if (!domainsColor[dataLimitMode.ALL]) domainsColor[dataLimitMode.ALL] = {}
    calculDomainsData(
      plotVariables,
      domains[dataLimitMode.ALL],
      domainsAutoScale[dataLimitMode.ALL],
      domainsColor[dataLimitMode.ALL],
      datas,
    )

    //GENERE DOMAINE FOR LAST 24H DATA
    const datasLast24H = filterLastData(24, dateVariableName, datas)
    if (!domains[dataLimitMode.LAST_24H]) domains[dataLimitMode.LAST_24H] = {}
    if (!domainsAutoScale[dataLimitMode.LAST_24H]) domainsAutoScale[dataLimitMode.LAST_24H] = {}
    if (!domainsColor[dataLimitMode.LAST_24H]) domainsColor[dataLimitMode.LAST_24H] = {}
    calculDomainsData(
      plotVariables,
      domains[dataLimitMode.LAST_24H],
      domainsAutoScale[dataLimitMode.LAST_24H],
      domainsColor[dataLimitMode.LAST_24H],
      datasLast24H,
    )

    //GENERE DOMAINE FOR LAST 12H DATA
    const datasLast12H = filterLastData(12, dateVariableName, datasLast24H)
    if (!domains[dataLimitMode.LAST_12H]) domains[dataLimitMode.LAST_12H] = {}
    if (!domainsAutoScale[dataLimitMode.LAST_12H]) domainsAutoScale[dataLimitMode.LAST_12H] = {}
    if (!domainsColor[dataLimitMode.LAST_12H]) domainsColor[dataLimitMode.LAST_12H] = {}
    calculDomainsData(
      plotVariables,
      domains[dataLimitMode.LAST_12H],
      domainsAutoScale[dataLimitMode.LAST_12H],
      domainsColor[dataLimitMode.LAST_12H],
      datasLast12H,
    )
  }
}

export const calculDomainsData = (
  plotVariables: Array<Plotvariable>,
  domains: any,
  domainsAutoScale: any,
  domainsColor: any,
  datas: Array<any>,
) => {
  if (datas.length > 0) {
    //genere les domaines pour les données de chacune des variables
    plotVariables.reduce((accumulateur, plotvariable) => {
      if (plotvariable.forceDomain)
        accumulateur[plotvariable.variable] = [plotvariable.minDomain, plotvariable.maxDomain]
      else {
        if (plotvariable.variables instanceof Array) {
          accumulateur[plotvariable.variable] = extent(
            plotvariable.variables.reduce(
              (acc, variable) =>
                [...acc, ...datas.map(d => d[variable])].filter(v => !mustIgnoreValue(v, plotvariable.logarithmeAxis)),
              [] as Array<any>,
            ),
          )
        } else {
          accumulateur[plotvariable.variable] = extent(
            datas.map(d => d[plotvariable.variable]).filter(v => !mustIgnoreValue(v, plotvariable.logarithmeAxis)),
          )
        }
      }
      return accumulateur
    }, domains)

    //genere les domaines autoScale pour les données de chacune des variables
    plotVariables.reduce((accumulateur, plotvariable) => {
      if (plotvariable.forceDomain)
        accumulateur[plotvariable.variable] = [plotvariable.minDomain, plotvariable.maxDomain]
      else {
        if (plotvariable.variables instanceof Array) {
          accumulateur[plotvariable.variable] = autoScaleDomain(
            plotvariable.variables.reduce(
              (acc, variable) => [
                ...acc,
                ...datas.map(d => {
                  if (d.hasOwnProperty('NAV_DEPTH')) {
                    return {
                      value: d[variable],
                      NAV_DEPTH: d['NAV_DEPTH'],
                    }
                  } else {
                    return { value: d[variable] }
                  }
                }),
              ],
              [] as Array<any>,
            ),
            d => d.value,
            plotvariable.logarithmeAxis,
          )
        } else {
          accumulateur[plotvariable.variable] = autoScaleDomain(
            datas,
            d => d[plotvariable.variable],
            plotvariable.logarithmeAxis,
          )
        }
      }
      return accumulateur
    }, domainsAutoScale)

    //genere les domaines pour l'echelle de couleur pour les données de chacune des variables
    plotVariables.reduce((accumulateur, plotvariable) => {
      if (plotvariable.forceDomain)
        accumulateur[plotvariable.variable] = [
          plotvariable.minDomain,
          (plotvariable.minDomain + plotvariable.maxDomain) / 2.0,
          plotvariable.maxDomain,
        ]
      else {
        if (plotvariable.variables instanceof Array) {
          accumulateur[plotvariable.variable] = autoScaleDomain3(
            plotvariable.variables.reduce(
              (acc, variable) => [
                ...acc,
                ...datas.map(d => {
                  if (d.hasOwnProperty('NAV_DEPTH')) {
                    return {
                      value: d[variable],
                      NAV_DEPTH: d['NAV_DEPTH'],
                    }
                  } else {
                    return { value: d[variable] }
                  }
                }),
              ],
              [] as Array<any>,
            ),
            d => d.value,
            plotvariable.logarithmeAxis,
          )
        } else {
          accumulateur[plotvariable.variable] = autoScaleDomain3(
            datas,
            d => d[plotvariable.variable],
            plotvariable.logarithmeAxis,
          )
        }
      }
      return accumulateur
    }, domainsColor)
  }
}

export const filterLastData = (lastDureeH: number, dateVariableName: string, datas: Array<any>) => {
  if (datas.length > 0) {
    const offsetH = 3600000 * lastDureeH
    const maxDateTime = datas.reduce(
      (max, current) => (max > current[dateVariableName].getTime() ? max : current[dateVariableName].getTime()),
      0,
    )
    return datas.filter(d => maxDateTime - d[dateVariableName].getTime() <= offsetH)
  }
  return []
}
